import { Button, List, Modal, Space, Tag, Typography, message } from 'antd';
import { LOGIN_PAGE, LOCAL_STORAGE_TOKEN_KEY } from 'commons';
import LOCALS from 'commons/locals';
import {
  CachedAccount,
  formatTokenExpiry,
  getCachedAccounts,
  getCurrentCachedAccount,
  isTokenExpired,
  removeCachedAccount,
  switchToCachedAccount,
} from 'commons/cached-accounts';
import i18n from 'i18n';
import { useCallback, useEffect, useState } from 'react';

type Props = {
  open: boolean;
  onClose: () => void;
  /** 登录页使用时：切换成功会跳首页；布局内使用时刷新当前页 */
  showAddAccount?: boolean;
};

const AccountSwitcherModal = ({
  open,
  onClose,
  showAddAccount = true,
}: Props) => {
  const [accounts, setAccounts] = useState<CachedAccount[]>([]);
  const [currentAdminId, setCurrentAdminId] = useState<number | null>(null);

  const refresh = useCallback(() => {
    setAccounts(getCachedAccounts());
    setCurrentAdminId(getCurrentCachedAccount()?.adminId ?? null);
  }, []);

  useEffect(() => {
    if (open) refresh();
  }, [open, refresh]);

  const handleSwitch = (account: CachedAccount) => {
    if (account.adminId === currentAdminId) {
      onClose();
      return;
    }
    if (isTokenExpired(account.expiresAt)) {
      message.warning(String(i18n.t(LOCALS.token_expired_relogin)));
      return;
    }
    try {
      switchToCachedAccount(account.adminId);
    } catch (err: any) {
      message.error(
        err?.message || String(i18n.t(LOCALS.switch_account_failed)),
      );
    }
  };

  return (
    <Modal
      title={i18n.t(LOCALS.switch_account)}
      open={open}
      onCancel={onClose}
      footer={[
        showAddAccount ? (
          <Button
            key="add"
            onClick={() => {
              window.location.href = LOGIN_PAGE;
            }}
          >
            {i18n.t(LOCALS.login_new_account)}
          </Button>
        ) : null,
        <Button key="close" type="primary" onClick={onClose}>
          {i18n.t(LOCALS.close)}
        </Button>,
      ].filter(Boolean)}
    >
      <Typography.Paragraph type="secondary">
        {i18n.t(LOCALS.switch_account_hint)}
      </Typography.Paragraph>
      <List
        bordered
        locale={{ emptyText: String(i18n.t(LOCALS.cached_accounts_empty)) }}
        dataSource={accounts}
        renderItem={(item) => {
          const expired = isTokenExpired(item.expiresAt);
          const isCurrent = item.adminId === currentAdminId;
          return (
            <List.Item
              actions={[
                <Button
                  key="switch"
                  type="link"
                  disabled={expired || isCurrent}
                  onClick={() => handleSwitch(item)}
                >
                  {isCurrent
                    ? i18n.t(LOCALS.current_account)
                    : i18n.t(LOCALS.switch)}
                </Button>,
                <Button
                  key="remove"
                  type="link"
                  danger
                  onClick={() => {
                    Modal.confirm({
                      title: i18n.t(LOCALS.remove_cached_account_title),
                      content: isCurrent
                        ? i18n.t(LOCALS.remove_cached_account_current_content)
                        : i18n.t(LOCALS.remove_cached_account_other_content),
                      onOk: () => {
                        removeCachedAccount(item.adminId);
                        if (isCurrent) {
                          window.localStorage.removeItem(
                            LOCAL_STORAGE_TOKEN_KEY,
                          );
                          window.location.href = LOGIN_PAGE;
                          return;
                        }
                        refresh();
                      },
                    });
                  }}
                >
                  {i18n.t(LOCALS.remove)}
                </Button>,
              ]}
            >
              <List.Item.Meta
                title={
                  <Space size={8} wrap>
                    <span>{item.nickName || item.username}</span>
                    {isCurrent ? (
                      <Tag color="blue">{i18n.t(LOCALS.current_account)}</Tag>
                    ) : null}
                    {expired ? (
                      <Tag color="error">{i18n.t(LOCALS.token_expired)}</Tag>
                    ) : null}
                  </Space>
                }
                description={
                  <div>
                    <div>{item.username}</div>
                    <div
                      style={{
                        color: expired ? '#ff4d4f' : '#8c8c8c',
                        marginTop: 4,
                      }}
                    >
                      {formatTokenExpiry(item.expiresAt)}
                    </div>
                  </div>
                }
              />
            </List.Item>
          );
        }}
      />
    </Modal>
  );
};

export default AccountSwitcherModal;
